import { Colors, Lightning } from '@lightningjs/sdk';
import Button from 'components/common/buttons/Button';
import { getImageTextureObj } from 'support/generalUtils';
import {
  getPromoHeroLandscapeUrl,
  getPromoHeroLogoUrl,
  getPromoHubLandscapeUrl,
} from 'support/cwImageUtils';
import { PromoLaneItem } from 'services/cwData';
import VisibilityIndicator from 'components/common/VisibilityIndicator';
import { constants } from 'aliases';
import TextLabel from './TextLabel';

export interface BillboardTemplateSpec
  extends Lightning.Component.TemplateSpec {
  mediaItem: PromoLaneItem | null;
  isHubBackground: boolean;
  isHero: boolean;
  carouselIndex: number | undefined;
  Background: {
    BackgroundContain: Lightning.textures.ImageTexture;
  };
  GradientPrimary: Lightning.Component;
  GradientSecondary: Lightning.Component;
  Content: {
    Logo: {
      LogoContain: Lightning.textures.ImageTexture;
    };
    Description: {
      Text: typeof TextLabel;
    };
    Button: typeof Button;
  };
  VisibilityIndicator: typeof VisibilityIndicator;
}

const BILLBOARD_WIDTH = 1920;
const BILLBOARD_HEIGHT = 768;

const GRADIENT_WIDTH = BILLBOARD_WIDTH;
const GRADIENT_HEIGHT = 200;
const GRADIENT_PRIMARY_Y = BILLBOARD_HEIGHT - GRADIENT_HEIGHT;
const GRADIENT_SECONDARY_Y = BILLBOARD_HEIGHT;

const CONTENT_PADDING_X = constants.sizing.navbar.collapsedWidth;

const TITLE_TOP_Y = 60;
const TITLE_HEIGHT = 52;

const CONTENT_MARGIN = 27;

const LOGO_Y = 60;
const LOGO_WIDTH = 565;
const LOGO_HEIGHT = 230;

const DESCRIPTION_HEIGHT = 60;
const DESCRIPTION_WIDTH = 565;
const DESCRIPTION_FONT_SIZE = 25;
const DESCRIPTION_LINE_HEIGHT = 30;

const BUTTON_FONT_SIZE = 23;
const BUTTON_PADDING = 12;
const BUTTON_MARGIN = 12;
const BUTTON_HEIGHT = 57;

export default class Billboard
  extends Lightning.Component<BillboardTemplateSpec>
  implements Lightning.Component.ImplementTemplateSpec<BillboardTemplateSpec>
{
  private _mediaItem: PromoLaneItem | null = null;
  private _backgroundImageUri = '';
  private _isHubBackground = false;
  private _notifyWhenFullyVisible = false;
  private _isSinglePromo = false;

  private _Background = this.getByRef('Background')!;
  private _GradientPrimary = this.getByRef('GradientPrimary')!;
  private _GradientSecondary = this.getByRef('GradientSecondary')!;
  private _BackgroundContain = this._Background.getByRef('BackgroundContain')!;
  private _Content = this.getByRef('Content')!;
  private _Logo = this._Content.getByRef('Logo')!;
  private _LogoContain = this._Logo.getByRef('LogoContain')!;
  private _Button = this._Content.getByRef('Button')!;
  private _Description = this._Content.getByRef('Description')!;
  private _DescriptionText = this._Description.getByRef('Text')!;
  protected _VisibilityIndicator = this.getByRef('VisibilityIndicator')!;

  isHero = false;
  carouselIndex: number | undefined;

  static get width() {
    return BILLBOARD_WIDTH;
  }

  static get height() {
    return BILLBOARD_HEIGHT;
  }

  get mediaItem() {
    return this._mediaItem;
  }

  set mediaItem(mediaItem: PromoLaneItem | null) {
    this._mediaItem = mediaItem;
    if (!mediaItem) return;

    const promoButton = mediaItem.promoButton ?? '';
    const promoDescription = mediaItem.promoDescription ?? '';

    this.updateBackground(mediaItem);
    this.updateLogo(mediaItem);
    this.updateDescription(promoDescription);
    this.updateButton(promoButton);

    this._VisibilityIndicator.patch({
      visible: this._notifyWhenFullyVisible && !!this._mediaItem,
    });
  }

  get notifyWhenFullyVisible() {
    return this._notifyWhenFullyVisible;
  }

  set notifyWhenFullyVisible(notifyWhenFullyVisible: boolean) {
    this._notifyWhenFullyVisible = notifyWhenFullyVisible;

    this._VisibilityIndicator.patch({
      visible: this._notifyWhenFullyVisible && !!this._mediaItem,
    });
  }

  get isHubBackground() {
    return this._isHubBackground;
  }

  set isHubBackground(isHubBackground: boolean) {
    this._isHubBackground = isHubBackground;
  }

  set isSinglePromo(isSinglePromo: boolean) {
    this._isSinglePromo = isSinglePromo;
  }

  static override _template(): Lightning.Component.Template<BillboardTemplateSpec> {
    return {
      Background: {
        w: BILLBOARD_WIDTH,
        h: BILLBOARD_HEIGHT,
        BackgroundContain: {},
      },
      // Two gradients are used here to better match the non-linear fading
      GradientPrimary: {
        y: GRADIENT_PRIMARY_Y,
        w: GRADIENT_WIDTH,
        h: GRADIENT_HEIGHT,
        rect: true,
        colorTop: Colors('appBackground').alpha(0).get(),
        colorBottom: Colors('appBackground').get(),
      },
      GradientSecondary: {
        y: GRADIENT_SECONDARY_Y,
        w: GRADIENT_WIDTH,
        h: GRADIENT_HEIGHT,
        rect: true,
        colorTop: Colors('appBackground').get(),
        colorBottom: Colors('appBackground').alpha(0).get(),
      },
      Content: {
        flex: {
          direction: 'column',
        },
        x: CONTENT_PADDING_X,
        Logo: {
          flexItem: {
            marginTop: LOGO_Y,
            marginBottom: CONTENT_MARGIN,
          },
          w: LOGO_WIDTH,
          h: LOGO_HEIGHT,
          LogoContain: {},
        },
        Description: {
          h: DESCRIPTION_HEIGHT,
          flex: {
            justifyContent: 'flex-end',
            direction: 'column',
          },
          flexItem: {
            marginBottom: CONTENT_MARGIN,
          },
          Text: {
            type: TextLabel,
            text: {
              fontSize: DESCRIPTION_FONT_SIZE,
              textColor: Colors('text').get(),
              wordWrapWidth: DESCRIPTION_WIDTH,
              lineHeight: DESCRIPTION_LINE_HEIGHT,
              maxLines: 2,
            },
          },
        },
        Button: {
          type: Button,
          height: BUTTON_HEIGHT,
          fontSize: BUTTON_FONT_SIZE,
          padding: BUTTON_PADDING,
          margin: BUTTON_MARGIN,
          signals: {
            $billboardSelected: '$billboardSelected',
          },
        },
      },
      VisibilityIndicator: {
        type: VisibilityIndicator,
        w: BILLBOARD_WIDTH,
        h: BILLBOARD_HEIGHT,
        signals: { indicateFullyVisible: '$indicateFullyVisible' },
        visible: false,
      },
    };
  }

  override _getFocused() {
    return this._Button;
  }

  private updateBackground(mediaItem: PromoLaneItem) {
    this._backgroundImageUri = this._isHubBackground
      ? getPromoHubLandscapeUrl(BILLBOARD_WIDTH, mediaItem.promoImageId)
      : getPromoHeroLandscapeUrl(BILLBOARD_WIDTH, mediaItem.promoImageId);

    this._BackgroundContain.patch(
      getImageTextureObj(
        this._backgroundImageUri,
        BILLBOARD_WIDTH,
        BILLBOARD_HEIGHT,
      ),
    );
    this._GradientPrimary.patch({ w: BILLBOARD_WIDTH });
    this._GradientSecondary.patch({ w: BILLBOARD_WIDTH });
  }

  private updateLogo(mediaItem: PromoLaneItem) {
    const logoY = this._isSinglePromo ? TITLE_TOP_Y + TITLE_HEIGHT : LOGO_Y;
    this._Logo.patch({
      x: 0,
      flexItem: {
        marginTop: logoY,
      },
    });

    this._LogoContain.patch(
      getImageTextureObj(
        getPromoHeroLogoUrl(LOGO_WIDTH, mediaItem.promoImageId),
        LOGO_WIDTH,
        LOGO_HEIGHT,
      ),
    );
  }

  private updateDescription(descriptionText: string) {
    this._DescriptionText.patch({ text: { text: descriptionText } });
  }

  private updateButton(buttonText: string) {
    if (!buttonText) {
      this._Button.patch({ visible: false, action: undefined });
    } else {
      this._Button.patch({
        visible: true,
        action: '$billboardSelected',
        label: buttonText,
      });
    }
  }

  private $indicateFullyVisible() {
    this.handleVisibilitySignal();
  }

  protected handleVisibilitySignal() {
    if (this._notifyWhenFullyVisible && this._mediaItem) {
      this.fireAncestors(
        '$onBillboardFullyVisible',
        this._mediaItem,
        this.carouselIndex ?? 0,
      );
    }
  }

  $billboardSelected() {
    if (!this._mediaItem) return;
    this.fireAncestors(
      '$onBillboardSelect',
      this._mediaItem,
      this.isHero,
      this._backgroundImageUri,
      this.carouselIndex,
    );
  }
}
